import { accessQueryParams, byNewestRule } from '@/function/begateway/rule.query';
import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import { writeAccessRule } from '@/store/api/begateway/begateway.access.cache';
import type { TypeAppDispatch } from '@/store/store';
import type { TypePageInfo } from '@/types/api/types.api';
import type {
    TypeAccessCheckInput,
    TypeAccessCheckResult,
    TypeAccessRule,
    TypeAccessRuleInput,
    TypeAccessRuleQuery,
    TypeAccessRuleUpdate,
} from '@/types/begateway/types.begateway.access';

type TypeAccessRuleIndex = { total: number; source: string | null; loadedAt: string | null };

type TypeAccessRuleList = { items: TypeAccessRule[]; index: TypeAccessRuleIndex | null };

const PAGE_SIZE = 200;

const MAX_PAGES = 50;

type TypeAccessPage = { items: TypeAccessRule[]; page: TypePageInfo | null; index: TypeAccessRuleIndex | null };

const ruleUrl = (id: string): string => `/begateway/access/${encodeURIComponent(id)}`;

const writeFromMutation = async (dispatch: TypeAppDispatch, queryFulfilled: Promise<{ data: TypeAccessRule }>) => {
    try {
        const { data } = await queryFulfilled;
        writeAccessRule(dispatch, data);
        dispatch(dashboardApi.util.invalidateTags([
            { type: 'GatewayAudit', id: data.id },
            ...(data.scope.endpointId ? [{ type: 'GatewayAudit' as const, id: data.scope.endpointId }] : []),
        ]));
    } catch {
    }
};

export const begatewayAccessApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listAccessRules: build.query<TypeAccessRuleList, TypeAccessRuleQuery>({
            queryFn: async (query) => {
                let index: TypeAccessRuleIndex | null = null;
                const walked = await walkPages<TypeAccessRule, TypeAccessPage>({
                    url: '/begateway/access',
                    fallback: 'Non è stato possibile leggere le regole dal gateway',
                    params: accessQueryParams(query),
                    pageSize: PAGE_SIZE,
                    maxPages: MAX_PAGES,
                    onPage: (body) => { index = body.index ?? index; },
                });
                if ('error' in walked) return walked;
                const items = walked.data;

                items.sort(byNewestRule);
                return { data: { items, index } };
            },
            providesTags: ['GatewayAccess'],
        }),

        readAccessRule: build.query<TypeAccessRule, string>({
            query: (id) => ({ url: ruleUrl(id) }),
            providesTags: (_result, _error, id) => [{ type: 'GatewayAccess', id }],
        }),

        createAccessRule: build.mutation<TypeAccessRule, TypeAccessRuleInput>({
            query: (rule) => ({ url: '/begateway/access', method: 'post', data: { rule } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => writeFromMutation(dispatch, queryFulfilled),
        }),

        updateAccessRule: build.mutation<TypeAccessRule, { id: string; rule: TypeAccessRuleUpdate }>({
            query: ({ id, rule }) => ({ url: ruleUrl(id), method: 'patch', data: { rule } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => writeFromMutation(dispatch, queryFulfilled),
        }),

        revokeAccessRule: build.mutation<TypeAccessRule, { id: string }>({
            query: ({ id }) => ({ url: `${ruleUrl(id)}/revoke`, method: 'post' }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => writeFromMutation(dispatch, queryFulfilled),
        }),

        checkAccess: build.mutation<TypeAccessCheckResult, TypeAccessCheckInput>({
            query: (check) => ({ url: '/begateway/access/check', method: 'post', data: { check } }),
        }),
    }),
});

export const {
    useListAccessRulesQuery,
    useReadAccessRuleQuery,
    useCreateAccessRuleMutation,
    useUpdateAccessRuleMutation,
    useRevokeAccessRuleMutation,
    useCheckAccessMutation,
} = begatewayAccessApi;

export default begatewayAccessApi;
